import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Trash2, AlertTriangle, CheckCircle2, Database, RefreshCw } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeader } from '../utils/authState';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

interface CorruptedRecord {
  key: string;
  id: string;
  type: 'company' | 'consulting_firm' | 'user' | 'company_data';
  name?: string;
  email?: string;
  subdomain?: string;
  issues: string[];
  rawData: any;
}

export function DataCleanupTool() {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [corruptedRecords, setCorruptedRecords] = useState<CorruptedRecord[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const scanForCorruptedData = async () => {
    setScanning(true);
    setError('');
    setSuccess('');
    setCorruptedRecords([]);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/scan-corrupted-data`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to scan for corrupted data');
      }

      const data = await response.json();
      setCorruptedRecords(data.corruptedRecords || []);
      // Only show success message if records were found
      if (data.corruptedRecords?.length > 0) {
        setSuccess(`Scan complete. Found ${data.corruptedRecords.length} corrupted records.`);
      }
    } catch (err: any) {
      console.error('Error scanning for corrupted data:', err);
      setError(err.message || 'Failed to scan for corrupted data');
    } finally {
      setScanning(false);
    }
  };

  const toggleRecord = (key: string) => {
    const newSelection = new Set(selectedRecords);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedRecords(newSelection);
  };

  const toggleAll = () => {
    if (selectedRecords.size === corruptedRecords.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(corruptedRecords.map(r => r.key)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRecords.size === 0) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const keysToDelete = Array.from(selectedRecords);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-corrupted-records`,
        {
          method: 'DELETE',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ keys: keysToDelete }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete corrupted records');
      }

      const data = await response.json();
      setSuccess(`Successfully deleted ${data.deletedCount} corrupted records.`);
      setSelectedRecords(new Set());
      setDeleteDialogOpen(false);
      setConfirmText('');
      
      // Refresh the list
      await scanForCorruptedData();
    } catch (err: any) {
      console.error('Error deleting corrupted records:', err);
      setError(err.message || 'Failed to delete corrupted records');
    } finally {
      setLoading(false);
    }
  };

  const getRecordLabel = (record: CorruptedRecord): string => {
    if (record.name) return record.name;
    if (record.email) return record.email;
    if (record.subdomain) return record.subdomain;
    return record.id || 'Unknown';
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'company': return 'Company';
      case 'consulting_firm': return 'Consulting Firm';
      case 'user': return 'User';
      case 'company_data': return 'Company Data';
      default: return type;
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'company': return 'bg-blue-100 text-blue-800';
      case 'consulting_firm': return 'bg-purple-100 text-purple-800';
      case 'user': return 'bg-green-100 text-green-800';
      case 'company_data': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Cleanup Tool
              </CardTitle>
              <CardDescription>
                Scan for and remove corrupted or incomplete database records
              </CardDescription>
            </div>
            <Button
              onClick={scanForCorruptedData}
              disabled={scanning}
              variant="outline"
            >
              {scanning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Scan for Issues
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>This tool scans for:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>Companies or consulting firms with missing/undefined names</li>
                <li>Companies with missing subdomains</li>
                <li>Users with missing email addresses</li>
                <li>Orphaned company_data records (no matching company)</li>
                <li>Records with corrupted JSON data</li>
              </ul>
            </AlertDescription>
          </Alert>

          {corruptedRecords.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedRecords.size === corruptedRecords.length && corruptedRecords.length > 0}
                    onCheckedChange={toggleAll}
                  />
                  <Label className="text-sm font-medium">
                    Select All ({corruptedRecords.length})
                  </Label>
                </div>
                {selectedRecords.size > 0 && (
                  <Button
                    onClick={handleDeleteSelected}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected ({selectedRecords.size})
                  </Button>
                )}
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto border rounded-lg p-4">
                {corruptedRecords.map((record) => (
                  <Card key={record.key} className="bg-red-50 border-red-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedRecords.has(record.key)}
                          onCheckedChange={() => toggleRecord(record.key)}
                        />
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(record.type)}`}>
                              {getTypeLabel(record.type)}
                            </span>
                            <span className="font-medium">{getRecordLabel(record)}</span>
                            {record.id && (
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                ID: {record.id.substring(0, 8)}...
                              </code>
                            )}
                          </div>
                          
                          <div className="text-sm">
                            <strong>Issues:</strong>
                            <ul className="list-disc list-inside mt-1 text-red-600">
                              {record.issues.map((issue, idx) => (
                                <li key={idx}>{issue}</li>
                              ))}
                            </ul>
                          </div>

                          <details className="text-xs">
                            <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                              View Raw Data
                            </summary>
                            <pre className="mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                              {JSON.stringify(record.rawData, null, 2)}
                            </pre>
                          </details>

                          <div className="text-xs text-gray-600">
                            <strong>Database Key:</strong> <code>{record.key}</code>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {!scanning && corruptedRecords.length === 0 && !error && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                No corrupted records found. Your database is clean.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Corrupted Records
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete {selectedRecords.size} corrupted database record(s).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> Deleting these records will permanently remove them from the database.
                Make sure you understand the impact before proceeding.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="confirm-text">
                Type <strong>DELETE {selectedRecords.size} RECORDS</strong> to confirm
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE X RECORDS"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConfirmText('');
              setDeleteDialogOpen(false);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={confirmText !== `DELETE ${selectedRecords.size} RECORDS` || loading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Records
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}